export type CompareOption = "time" | "targets";
export type AggregateLevel = "daily" | "weekly" | "monthly";
export type SitewideMetric = "spend" | "mer" | "amer" | "cac" | "aov";
export type FacebookMetric = "spend" | "roas" | "cpa" | "aov" | "cpc" | "cpm" | "ctr" | "cvr" | "impressions" | "clicks" | "purchases" | "purchase_conversion_value";
export type GoogleMetric = "spend" | "purchase_conversion_value" | "roas" | "cpa" | "aov" | "cpc" | "ctr" | "cvr" | "impressions" | "clicks" | "conversions";
export type Region = "North America" | "Europe" | "UK & Ireland" | "Australia & NZ" | "Asia Pacific" | "Others";

export interface SitewideDataPoint {
  date: string;
  aggregate_level: AggregateLevel;
  revenue: string;
  total_spend: string;
  mer: string;
  amer: string;
  cac: string;
  aov: string;
  region: string;
}

export interface SitewideResponse {
  data: {
    merchant: SitewideDataPoint[];
    benchmark: SitewideDataPoint[];
  };
}

export interface FacebookAggregate {
  date: string;
  aggregate_level: AggregateLevel;
  spend: string;
  purchase_conversion_value: string;
  purchases: string;
  roas: string;
  cpa: string;
  aov: string;
  cpc: string;
  cpm: string;
  ctr: string;
  cvr: string;
  impressions: string;
  clicks: string;
  reach: string;
  frequency: string;
  add_to_carts: string;
  checkout_initiated: string;
  currency: string;
  region: Region;
}

export interface FacebookResponse {
  data: {
    merchant: FacebookAggregate[];
    benchmark: FacebookAggregate[];
  };
}

export interface GoogleAggregate {
  date: string;
  aggregate_level: AggregateLevel;
  spend: string;
  purchase_conversion_value: string;
  roas: string;
  cpa: string;
  aov: string;
  cpc: string;
  ctr: string;
  cvr: string;
  impressions: string;
  clicks: string;
  conversions: string;
  region: string;
}

export interface GoogleResponse {
  data: {
    merchant: GoogleAggregate[];
    benchmark: GoogleAggregate[];
  };
}

type GetFunction = <T>(url: string) => Promise<T>;

export class CompareService {
  private get: GetFunction;

  constructor(getFn: GetFunction) {
    this.get = getFn;
  }

  async getCompareChart(params: {
    merchantId: string;
    start: string;
    end: string;
    compareOption?: CompareOption;
    region?: Region;
  }): Promise<SitewideResponse> {
    const { merchantId, start, end, compareOption = "time", region } = params;
    
    let url = `aggregates/sitewide?merchantId=${merchantId}&startDate=${start}&endDate=${end}`;
    
    if (region) {
      url += `&region=${encodeURIComponent(region)}`;
    }
    
    return this.get<SitewideResponse>(url);
  }

  async getFacebookCompareChart(params: {
    merchantId: string;
    startDate: string;
    endDate: string;
    aggregateLevel?: AggregateLevel;
    region?: Region;
  }): Promise<FacebookResponse> {
    const { merchantId, startDate, endDate, aggregateLevel = "daily", region } = params;
    
    let url = `aggregates/facebook?merchantId=${merchantId}&startDate=${startDate}&endDate=${endDate}&aggregateLevel=${aggregateLevel}`;
    
    if (region) {
      url += `&region=${encodeURIComponent(region)}`;
    }
    
    return this.get<FacebookResponse>(url);
  }

  async getGoogleCompareChart(params: {
    merchantId: string;
    startDate: string;
    endDate: string;
    aggregateLevel?: AggregateLevel;
    region?: Region;
  }): Promise<GoogleResponse> {
    const { merchantId, startDate, endDate, aggregateLevel = "daily", region } = params;

    let url = `aggregates/google?merchantId=${merchantId}&startDate=${startDate}&endDate=${endDate}&aggregateLevel=${aggregateLevel}`;

    if (region) {
      url += `&region=${encodeURIComponent(region)}`;
    }

    return this.get<GoogleResponse>(url);
  }
} 